import { API_BASE_URL } from '../config/api';
import { redirectToLoginAfterLogout } from '../config/publicEnv';

// Authentication service for Pharmacy Management System

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  message: string;
  accessToken: string;
  refreshToken: string;
  admin?: any;
  staff?: any;
  role: string;
}

export interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
  /** Main SuperAdmin (God-created); sub SuperAdmins are false */
  isMainSuperAdmin?: boolean;
  adminId?: string;
  staffRole?: string;
  staffRoles?: string[];
  pharmacyId?: string;
  clinicId?: string;
  clinic?: string[];
  /** Pharmacy is retail-facing (billing, prescriptions, inventory, etc.) */
  isRetail?: boolean;
  /** Pharmacy does vendor/procurement (purchase orders, bills, account master, etc.) */
  isVendor?: boolean;
  /** Feature permissions (e.g. banking, staff_management). Admin has all by default. */
  pharmacyPermissions?: string[];
}

const LAUNCHER_TOKEN_TYPE = 'launcher_sso';

/** Higher privilege first — used when a user has multiple staffRoles (e.g. Doctor + Admin). */
const ROLE_PRIORITY = [
  'God',
  'SuperAdmin',
  'EntityAdmin',
  'PharmaAdmin',
  'Admin',
  'Pharmacist',
  'Receptionist',
  'LabManager',
  'Pathologist',
  'LabTechnician',
  'LabAssistant',
  'Nurse',
  'Doctor',
  'Staff',
  'Other',
] as const;

function roleRank(role: string): number {
  const idx = ROLE_PRIORITY.indexOf(role as (typeof ROLE_PRIORITY)[number]);
  return idx === -1 ? ROLE_PRIORITY.length : idx;
}

/** Pick the highest-privilege role from a list so Doctor does not mask SuperAdmin/Admin. */
export function pickPrimaryRole(roles: Array<string | undefined | null>): string {
  const cleaned = roles.filter((r): r is string => typeof r === 'string' && r.trim().length > 0);
  if (cleaned.length === 0) return 'Staff';
  return cleaned.reduce((best, next) => (roleRank(next) < roleRank(best) ? next : best));
}

/** Roles that may use HYGO Pharmacy (admins + pharmacy operational staff). */
const PHARMACY_ACCESS_ROLES = new Set([
  'God',
  'SuperAdmin',
  'EntityAdmin',
  'PharmaAdmin',
  'Admin',
  'Pharmacist',
  'Receptionist',
  'Pharmacy',
]);

function collectUserRoles(user: Pick<User, 'role' | 'staffRole' | 'staffRoles'>): string[] {
  return [user.role, user.staffRole, ...(Array.isArray(user.staffRoles) ? user.staffRoles : [])].filter(
    (r): r is string => typeof r === 'string' && r.trim().length > 0,
  );
}

/** True if the account has pharmacy privilege (role and/or granted permissions). */
export function hasPharmacyAppAccess(user: User | null | undefined): boolean {
  if (!user) return false;
  const roles = collectUserRoles(user);
  if (roles.some((r) => PHARMACY_ACCESS_ROLES.has(r))) return true;
  return Array.isArray(user.pharmacyPermissions) && user.pharmacyPermissions.length > 0;
}

export function isDoctorWithoutPharmacyAccess(user: User | null | undefined): boolean {
  if (!user) return false;
  if (hasPharmacyAppAccess(user)) return false;
  return collectUserRoles(user).some((r) => r === 'Doctor');
}

const DOCTOR_NO_PHARMACY_ACCESS_MESSAGE =
  'Doctor accounts do not have access to HYGO Pharmacy. Ask your admin to grant pharmacy access, or sign in with a pharmacy account.';

function decodeJwtSegment(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Launcher SSO tokens and raw access tokens both arrive as `?token=`, so the
 * payload `type` claim is the only way to tell them apart.
 */
export function isLauncherSsoToken(token: string): boolean {
  return decodeJwtSegment(token)?.type === LAUNCHER_TOKEN_TYPE;
}

interface LauncherSessionPayload {
  accessToken?: string;
  refreshToken?: string;
  role?: string;
  clinicId?: string;
  pharmacyId?: string;
  staff?: Record<string, unknown>;
  admin?: Record<string, unknown>;
}

/** Session payload lives in `data`; older builds put the object in `message`. */
function extractLauncherSession(body: unknown): LauncherSessionPayload | null {
  const envelope = body as { data?: unknown; message?: unknown } | null;
  if (envelope?.data && typeof envelope.data === 'object') {
    return envelope.data as LauncherSessionPayload;
  }
  if (envelope?.message && typeof envelope.message === 'object') {
    return envelope.message as LauncherSessionPayload;
  }
  return null;
}

class AuthService {
  private deviceId: string;
  private static readonly LOGIN_FALLBACK_STATUSES = new Set([401, 404]);
  
  constructor() {
    // Generate or retrieve device ID
    this.deviceId = this.getOrCreateDeviceId();
  }

  private getOrCreateDeviceId(): string {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      // Generate a unique device ID
      deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  private normalizeLoginUser(rawUser: Record<string, unknown>, role?: string): User {
    const staffRolesList = Array.isArray(rawUser.staffRoles)
      ? rawUser.staffRoles.filter((r): r is string => typeof r === 'string')
      : [];
    // Prefer highest privilege so Doctor+Admin / Doctor+SuperAdmin is not treated as Doctor-only.
    const normalizedRole = pickPrimaryRole([
      role,
      typeof rawUser.role === 'string' ? rawUser.role : undefined,
      typeof rawUser.staffRole === 'string' ? rawUser.staffRole : undefined,
      ...staffRolesList,
    ]);
    const name =
      (typeof rawUser.name === 'string' && rawUser.name) ||
      (typeof rawUser.fullName === 'string' && rawUser.fullName) ||
      (typeof rawUser.email === 'string' && rawUser.email) ||
      'HYGO User';
    const baseUser = rawUser as unknown as User;
    return {
      ...baseUser,
      role: normalizedRole,
      staffRole: pickPrimaryRole([
        typeof rawUser.staffRole === 'string' ? rawUser.staffRole : undefined,
        ...staffRolesList,
        normalizedRole,
      ]),
      staffRoles: staffRolesList.length > 0 ? staffRolesList : baseUser.staffRoles,
      name,
    };
  }

  private clearLocalSession(): void {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('userRole');
    sessionStorage.removeItem('deviceId');
  }

  /** Doctors may enter pharmacy only when they also have admin/pharmacy role or permissions. */
  private assertPharmacyLoginAllowed(user: User): void {
    if (isDoctorWithoutPharmacyAccess(user)) {
      this.clearLocalSession();
      throw new Error(DOCTOR_NO_PHARMACY_ACCESS_MESSAGE);
    }
  }

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      let data: LoginResponse;
      try {
        // Prefer Admin login so SuperAdmin/God who are also Doctors (Staff) keep SuperAdmin in pharmacy.
        data = await this.loginWithEndpoint('/api/V0/Admin/login', credentials);
      } catch (error) {
        const maybeStatus = (error as { status?: number } | null)?.status;
        if (!maybeStatus || !AuthService.LOGIN_FALLBACK_STATUSES.has(maybeStatus)) {
          throw error;
        }
        // Fallback for pharmacy staff stored in Staff collection.
        data = await this.loginWithEndpoint('/api/V0/Staff/login', credentials);
      }

      const rawUser = data.staff || data.admin;
      if (rawUser) {
        const user = this.normalizeLoginUser(rawUser, data.role);
        this.assertPharmacyLoginAllowed(user);
        if (data.accessToken && data.refreshToken) {
          sessionStorage.setItem('accessToken', data.accessToken);
          sessionStorage.setItem('refreshToken', data.refreshToken);
          sessionStorage.setItem('deviceId', this.deviceId);
          sessionStorage.setItem('user', JSON.stringify(user));
          sessionStorage.setItem('userRole', user.role);
        }
      }

      return data;
    } catch (error) {
      // If it's already an Error with a message, rethrow it
      if (error instanceof Error) {
        console.error('Login error:', error.message);
        throw error;
      }
      // Handle unexpected error types
      console.error('Login error:', error);
      throw new Error('An unexpected error occurred during login. Please try again.');
    }
  }

  async loginWithToken(accessToken: string, refreshToken?: string, target?: string): Promise<User> {
    const validated = await this.validateOrDecodeToken(accessToken);
    const rawUserType = String(validated.userType || '').toLowerCase();
    const derivedRole = this.deriveRole(rawUserType, target);
    const user: User = {
      _id: validated._id,
      name: validated.name || validated.email || 'HYGO User',
      email: validated.email || '',
      role: derivedRole,
      staffRole: derivedRole,
    };

    this.assertPharmacyLoginAllowed(user);

    sessionStorage.setItem('accessToken', accessToken);
    if (refreshToken) {
      sessionStorage.setItem('refreshToken', refreshToken);
    } else {
      sessionStorage.removeItem('refreshToken');
    }
    sessionStorage.setItem('deviceId', this.deviceId);
    sessionStorage.setItem('user', JSON.stringify(user));
    sessionStorage.setItem('userRole', derivedRole);
    return user;
  }

  /**
   * Consume a single-use launcher SSO token from HYGO Launcher Pad.
   *
   * Unlike loginWithToken, the backend returns the full staff/admin record here,
   * so the stored user keeps pharmacyId, clinicId, isRetail, isVendor and
   * pharmacyPermissions — the fields the sidebar and permission hooks rely on.
   */
  async loginWithLauncherToken(launcherToken: string): Promise<User> {
    const response = await fetch(
      `${API_BASE_URL}/api/V0/auth/validate-launcher-token?token=${encodeURIComponent(launcherToken)}`,
      { headers: { 'X-Device-ID': this.deviceId } },
    );
    const body = await response.json().catch(() => ({}));
    const session = extractLauncherSession(body);

    if (!response.ok || !session?.accessToken || !session.refreshToken) {
      const reason =
        (body as { message?: unknown })?.message ??
        (body as { data?: unknown })?.data;
      throw new Error(
        typeof reason === 'string' && reason
          ? reason
          : 'This sign-in link is invalid or has already been used.',
      );
    }

    const rawUser = session.staff ?? session.admin;
    if (!rawUser) {
      throw new Error('Launcher session did not include a pharmacy user.');
    }

    const user = this.normalizeLoginUser(rawUser, session.role);
    // Envelope scope wins: the stored record can lag behind a scoped handoff.
    if (session.pharmacyId) user.pharmacyId = String(session.pharmacyId);
    if (session.clinicId) user.clinicId = String(session.clinicId);

    this.assertPharmacyLoginAllowed(user);

    sessionStorage.setItem('accessToken', session.accessToken);
    sessionStorage.setItem('refreshToken', session.refreshToken);
    sessionStorage.setItem('deviceId', this.deviceId);
    sessionStorage.setItem('user', JSON.stringify(user));
    sessionStorage.setItem('userRole', user.role);

    return user;
  }

  private async loginWithEndpoint(endpoint: string, credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Device-ID': this.deviceId,
      },
      body: JSON.stringify(credentials),
    });

    let payload: any = {};
    try {
      payload = await response.json();
    } catch {
      payload = {};
    }

    if (!response.ok) {
      const error = new Error(payload.message || payload.error || response.statusText || 'Login failed') as Error & { status?: number };
      error.status = response.status;
      throw error;
    }

    return payload as LoginResponse;
  }

  async logout(): Promise<void> {
    try {
      const token = this.getAccessToken();
      if (token) {
        try {
          await fetch(`${API_BASE_URL}/api/V0/Admin/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        } catch (error) {
          console.warn('Logout API call failed:', error);
        }
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearLocalSession();
    }
  }

  /** Request password reset link; backend sends email (Staff/Receptionist). */
  async forgotPassword(email: string): Promise<void> {
    const res = await fetch(`${API_BASE_URL}/api/V0/Admin/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.message || 'Request failed');
  }

  /** Set new password using token from reset link (Staff/Receptionist). */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    const res = await fetch(`${API_BASE_URL}/api/V0/Admin/reset-staff-password/${encodeURIComponent(token)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newPassword }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.message || 'Reset failed');
  }

  getAccessToken(): string | null {
    return sessionStorage.getItem('accessToken');
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem('refreshToken');
  }

  getUser(): User | null {
    const userStr = sessionStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  getUserRole(): string | null {
    return sessionStorage.getItem('userRole');
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  getDeviceId(): string {
    return this.deviceId;
  }

  // Make authenticated requests with device ID
  async authenticatedRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('No access token available. Please login again.');
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Device-ID': this.deviceId,
        ...options.headers,
      },
    });

    // Check if session was invalidated (device mismatch)
    if (response.status === 401) {
      const data = await response.json().catch(() => ({}));
      if (data.code === 'DEVICE_MISMATCH' || data.message?.includes('device')) {
        // Clear tokens and redirect to login
        this.logout();
        redirectToLoginAfterLogout();
        throw new Error('Session invalidated. Another device logged in.');
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || 'Request failed');
    }

    return response.json();
  }

  // Check session validity periodically
  startSessionMonitor(callback?: () => void): NodeJS.Timeout {
    return setInterval(async () => {
      try {
        if (typeof document !== 'undefined' && document.hidden) {
          return;
        }
        const token = this.getAccessToken();
        if (!token) return;

        const response = await fetch(`${API_BASE_URL}/api/V0/auth/validate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Device-ID': this.deviceId,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ accessToken: token }),
        });

        if (response.status === 401) {
          const data = await response.json().catch(() => ({}));
          if (data.code === 'DEVICE_MISMATCH') {
            this.logout();
            if (callback) callback();
            redirectToLoginAfterLogout();
          }
        }
      } catch (error) {
        console.error('Session validation error:', error);
      }
    }, 3000000); // Check every 3000 seconds
  }

  private deriveRole(rawUserType: string, target?: string): string {
    const raw = String(rawUserType || '').toLowerCase().replace(/\s+/g, '');

    // Preserve admin hierarchy — do not collapse SuperAdmin → Admin or let target=doctor win.
    if (raw === 'god') return 'God';
    if (raw === 'superadmin' || raw.includes('superadmin')) return 'SuperAdmin';
    if (raw === 'entityadmin') return 'EntityAdmin';
    if (raw === 'pharmaadmin') return 'PharmaAdmin';
    if (raw === 'admin') return 'Admin';

    if (target === 'receptionist') return 'Receptionist';
    if (target === 'doctor') return 'Doctor';
    if (raw.includes('pharmacist')) return 'Pharmacist';
    if (raw.includes('pharma')) return 'Pharmacy';
    if (raw.includes('reception')) return 'Receptionist';
    if (raw.includes('doctor')) return 'Doctor';
    if (raw.includes('staff')) return 'Staff';
    if (raw.includes('admin')) return 'Admin';
    return 'Admin';
  }

  private async validateOrDecodeToken(accessToken: string): Promise<{ _id: string; userType?: string; email?: string; name?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/V0/auth/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessToken }),
      });
      const body = await response.json().catch(() => ({} as any));
      const user = body?.data?.user;
      if (response.ok && user?._id) {
        return {
          _id: user._id,
          userType: user.userType,
          email: user.email,
          name: user.name,
        };
      }
    } catch {
      // Fall back to local decode
    }

    const decoded = this.decodeJwtPayload(accessToken);
    if (!decoded?._id) {
      throw new Error('SSO token is invalid or expired.');
    }
    return {
      _id: decoded._id,
      userType: decoded.userType,
      email: decoded.Email,
      name: decoded.name,
    };
  }

  private decodeJwtPayload(token: string): { _id?: string; userType?: string; Email?: string; name?: string; exp?: number } | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      const decoded = atob(padded);
      const parsed = JSON.parse(decoded) as { _id?: string; userType?: string; Email?: string; name?: string; exp?: number };
      const now = Math.floor(Date.now() / 1000);
      if (!parsed.exp || parsed.exp <= now) return null;
      return parsed;
    } catch {
      return null;
    }
  }
}

export const authService = new AuthService();
export default authService;

